import { ApolloLink, Observable } from '@apollo/client'
import { isEmpty, noop } from 'lodash-es'

const SUBSCRIPTION_ID_HEADER = 'X-Subscription-ID'

export interface PusherUpdatePayload {
  /** `false` on the server's final update — the signal to close the subscription. */
  more?: boolean
  result?: ApolloLink.Result
  /** Sent in place of `result` when graphql-pro is configured to compress payloads. */
  compressed_result?: string
}

interface PusherChannel {
  bind: (eventName: string, callback: (payload: PusherUpdatePayload) => void) => void
}

interface PusherSubscriber {
  subscribe: (channelName: string) => PusherChannel
  unsubscribe: (channelName: string) => void
}

interface CreatePusherLinkParams {
  pusher: PusherSubscriber
}

/**
 * Structural stand-in for the observer an `Observable` hands its subscriber function — rxjs's
 * `Subscriber` satisfies it, and naming it here keeps the link off the library-specific type.
 */
interface ResultObserver {
  next: (result: ApolloLink.Result) => void
  error: (error: Error) => void
  complete: () => void
}

interface Unsubscribable {
  unsubscribe: () => void
}

interface HeaderReader {
  get: (name: string) => string | null
}

interface ResponseLike {
  headers: HeaderReader
}

const isResponseLike = (value: unknown): value is ResponseLike => {
  if (typeof value !== 'object' || value === null || !('headers' in value)) {
    return false
  }

  const { headers } = value

  return typeof headers === 'object' && headers !== null && 'get' in headers && typeof headers.get === 'function'
}

/**
 * graphql-pro answers a subscription request over HTTP and names the Pusher channel to listen on in
 * this response header; a plain query or mutation comes back without it.
 *
 * Duck-typed rather than `instanceof Response` — a proxied, polyfilled, or cross-realm response
 * would fail an identity check and silently downgrade a subscription to a one-shot query.
 */
const getSubscriptionChannel = (operation: ApolloLink.Operation): string | null => {
  const response: unknown = operation.getContext().response

  return isResponseLike(response) ? response.headers.get(SUBSCRIPTION_ID_HEADER) : null
}

/** The response that registers a subscription is an empty ack — anything else must reach the consumer. */
const isRegistrationAck = (result: ApolloLink.Result): boolean => isEmpty(result.data) && isEmpty(result.errors)

/**
 * Bridges graphql-pro's Pusher-backed subscriptions into the Apollo link chain.
 *
 * Queries and mutations pass straight through. A subscription request is answered over HTTP with an
 * `X-Subscription-ID` header naming a Pusher channel; this link then joins that channel and emits
 * every `update` event until the server sends one with `more: false`.
 *
 * Unsubscribing leaves the Pusher channel and drops the forwarded request, so tearing down a
 * subscription before its response arrives aborts the in-flight fetch rather than leaking it.
 *
 * Vendored port of `graphql-ruby-client`'s `PusherLink`, which is unmaintained against Apollo v4.
 */
export const createPusherLink = ({ pusher }: CreatePusherLinkParams) =>
  new ApolloLink(
    (operation, forward) =>
      new Observable<ApolloLink.Result>((observer: ResultObserver) => {
        let subscribedChannelName: string | null = null
        // Stands in until `forward` returns: under rxjs a synchronously-completing link below —
        // `ApolloLink.empty()`, a split branch, a stubbed transport — runs this teardown before the
        // assignment lands, and reading the real subscription there would throw.
        let subscription: Unsubscribable = { unsubscribe: noop }

        subscription = forward(operation).subscribe({
          next: (result: ApolloLink.Result) => {
            // Registration happens once. Later payloads on a live subscription are not part of this
            // stream, while a plain multipart query never registers and keeps forwarding every chunk.
            if (subscribedChannelName) {
              return
            }

            const channelName = getSubscriptionChannel(operation)

            if (!channelName) {
              observer.next(result)

              return
            }

            // Recorded before subscribing so a throw from pusher-js still leaves the teardown a
            // channel to clean up.
            subscribedChannelName = channelName
            const channel = pusher.subscribe(channelName)

            // Bound before the initial payload goes out — a consumer that unsubscribes from that
            // payload would otherwise have us bind onto a channel the teardown already left.
            channel.bind('update', (payload) => {
              if (payload.compressed_result) {
                observer.error(new Error('Received a compressed_result payload, which this link cannot decompress.'))

                return
              }

              if (payload.result) {
                observer.next(payload.result)
              }

              // Only an explicit `false` ends the stream; a missing `more` must not read as one.
              if (payload.more === false) {
                observer.complete()
              }
            })

            if (!isRegistrationAck(result)) {
              observer.next(result)
            }
          },
          error: (error: Error) => observer.error(error),
          complete: () => {
            // A subscription outlives the request that registered it — only an `update` with
            // `more: false` ends it. Anything else is a query or mutation, whose completion is the
            // transport's to signal, and swallowing it would strand the consumer.
            if (!subscribedChannelName) {
              observer.complete()
            }
          },
        })

        // Completing runs this teardown too, so leaving the channel lives here rather than at every
        // call site that ends the subscription.
        return () => {
          subscription.unsubscribe()

          if (subscribedChannelName) {
            pusher.unsubscribe(subscribedChannelName)
          }
        }
      }),
  )
